import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { I18N_MANUAL_SELECTION_KEY } from '../../constants/i18n';
import { useAuthStore } from '../../store/authStore';
import { publicService } from '../../services/publicService';
import { LoadingSpinner } from '../../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';
import {

  User,
  Mail,
  Lock,
  Phone,
  Building2,
  Shield,
  Eye,
  EyeOff,
  ArrowRight,
  Globe,
  UserPlus
} from 'lucide-react';
import type { Club } from '../../types/club';
import type { Role } from '../../types/user';

const getRoleDisplayName = (nombre: string): string => {
  if (!nombre) return "";
  const roleNames: Record<string, string> = {
    admin: "Administrador",
    director_deportivo: "Director Deportivo",
    entrenador: "Entrenador",
    scout: "Scout",
    analista: "Analista",
    medico: "Médico",
    fisioterapeuta: "Fisioterapeuta",
    preparador_fisico: "Preparador Físico",
    usuario: "Usuario",
    segundo_entrenador: "Segundo Entrenador",
    fisio: "Fisioterapeuta",
    preparador: "Preparador",
    readaptador: "Readaptador",
    nutricionista: "Nutricionista",
    jugador: "Jugador",
    secretaria_tecnica: "Secretaria Técnica"
  };
  return roleNames[nombre] || nombre.charAt(0).toUpperCase() + nombre.slice(1).replace(/_/g, ' ');
};


const RegisterPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { register, isLoading, error, clearError } = useAuthStore();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    preferred_language: 'es',
    club_id: '',
    role_id: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    minLength: false,
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSpecial: false
  });
  const [clubs, setClubs] = useState<Club[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoadingData(true);
      const [clubsData, rolesData] = await Promise.all([
        publicService.getAllClubs(),
        publicService.getRoles()
      ]);
      setClubs(clubsData);
      setRoles(rolesData);
    } catch (error: any) {
      toast.error(error.message || 'Error loading data');
    } finally {
      setLoadingData(false);
    }
  };

  const checkPasswordStrength = (password: string) => {
    const strength = {
      minLength: password.length >= 8,
      hasUpper: /[A-Z]/.test(password),
      hasLower: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
    setPasswordStrength(strength);
    return strength;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'password') {
      checkPasswordStrength(value);
    }
    if (error) clearError();
  };

  const validatePassword = (password: string) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('Al menos 8 caracteres');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Al menos una letra mayúscula');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Al menos una letra minúscula');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Al menos un número');
    }
    
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('Al menos un carácter especial (!@#$%^&*...)');
    }
    
    return errors;
  };

  const validateForm = () => {
    if (!formData.username.trim()) {
      toast.error(t('auth.username') + ' es requerido');
      return false;
    }
    
    if (!formData.email.trim()) {
      toast.error(t('auth.email') + ' es requerido');
      return false;
    }
    
    if (!formData.password) {
      toast.error(t('auth.password') + ' es requerida');
      return false;
    }
    
    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      toast.error('La contraseña debe tener: ' + passwordErrors.join(', '));
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('auth.passwordMismatch'));
      return false;
    }
    
    if (!formData.first_name.trim()) {
      toast.error(t('auth.firstName') + ' es requerido');
      return false;
    }
    
    if (!formData.last_name.trim()) {
      toast.error(t('auth.lastName') + ' es requerido');
      return false;
    }
    
    if (!formData.club_id) {
      toast.error(t('auth.selectClub'));
      return false;
    }
    
    if (!formData.role_id) {
      toast.error(t('auth.selectRole'));
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      const { confirmPassword, ...registerData } = formData;
      await register(registerData);
      toast.success(t('auth.registerSuccess'));
      navigate('/login');
    } catch (error: any) {
      toast.error(error.message || t('auth.registerError'));
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
    // Mark as manual selection
    localStorage.setItem(I18N_MANUAL_SELECTION_KEY, 'true');
    setFormData(prev => ({ ...prev, preferred_language: newLang }));
  };

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative" style={{ 
      background: 'linear-gradient(180deg, #000000 0%, #0F2A7D 50%, #12182E 100%)'
    }}>
      {/* Overlay sutil para profundidad y textura */}
      <div className="absolute inset-0 bg-black/10"></div>
      
      {/* Efectos de partículas sutiles con colores del logo */}
      <div className="absolute inset-0 opacity-15">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white rounded-full animate-pulse"></div>
        <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-blue-200 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-white rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-1/3 right-1/4 w-1 h-1 bg-blue-100 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute top-1/2 left-1/5 w-1 h-1 bg-white/70 rounded-full animate-pulse" style={{ animationDelay: '3s' }}></div>
        <div className="absolute top-3/4 right-1/5 w-1.5 h-1.5 bg-blue-200/80 rounded-full animate-pulse" style={{ animationDelay: '1.5s' }}></div>
      </div>

      <div className="relative w-full max-w-2xl">
        {/* Language Toggle */}
        <div className="absolute top-0 right-0 -mt-16">
          <button
            onClick={toggleLanguage}
            className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg backdrop-blur-sm transition-all duration-300 border border-white/20 hover:border-white/30"
          >
            <Globe className="w-4 h-4" />
            <span className="text-sm font-medium">{i18n.language.toUpperCase()}</span>
          </button>
        </div>

        {/* Register Card */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #0F2A7D 0%, #0A1F5C 100%)' }}>
                <UserPlus className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {t('auth.createAccount')}
            </h1>
            <p className="text-blue-100">
              Únete a la plataforma integral para clubes deportivos
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('auth.firstName')} *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                    placeholder={t('auth.firstName')}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('auth.lastName')} *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                    placeholder={t('auth.lastName')}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Account Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('auth.username')} *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                    placeholder={t('auth.username')}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('auth.email')} *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                    placeholder={t('auth.email')}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Password Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('auth.password')} *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="w-full pl-11 pr-11 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                    placeholder={t('auth.password')}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-300 hover:text-blue-200 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {formData.password && (
                  <div className="mt-2 p-3 bg-white/5 rounded-lg border border-white/10">
                    <div className="text-xs text-blue-200 mb-2">Requisitos de contraseña:</div>
                    <div className="space-y-1 text-xs">
                      <div className={`flex items-center ${passwordStrength.minLength ? 'text-green-300' : 'text-red-300'}`}>
                        <span className="mr-2">{passwordStrength.minLength ? '✓' : '×'}</span>
                        Al menos 8 caracteres
                      </div>
                      <div className={`flex items-center ${passwordStrength.hasUpper ? 'text-green-300' : 'text-red-300'}`}>
                        <span className="mr-2">{passwordStrength.hasUpper ? '✓' : '×'}</span>
                        Una letra mayúscula
                      </div>
                      <div className={`flex items-center ${passwordStrength.hasLower ? 'text-green-300' : 'text-red-300'}`}>
                        <span className="mr-2">{passwordStrength.hasLower ? '✓' : '×'}</span>
                        Una letra minúscula
                      </div>
                      <div className={`flex items-center ${passwordStrength.hasNumber ? 'text-green-300' : 'text-red-300'}`}>
                        <span className="mr-2">{passwordStrength.hasNumber ? '✓' : '×'}</span>
                        Un número
                      </div>
                      <div className={`flex items-center ${passwordStrength.hasSpecial ? 'text-green-300' : 'text-red-300'}`}>
                        <span className="mr-2">{passwordStrength.hasSpecial ? '✓' : '×'}</span>
                        Un carácter especial (!@#$%...)
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('auth.confirmPassword')} *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className="w-full pl-11 pr-11 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                    placeholder={t('auth.confirmPassword')}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-300 hover:text-blue-200 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {t('auth.phone')}
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                  placeholder={t('auth.phone')}
                />
              </div>
            </div>

            {/* Club and Role */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('auth.club')} *
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
                  <select
                    value={formData.club_id}
                    onChange={(e) => handleInputChange('club_id', e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                    required
                  >
                    <option value="">{t('auth.selectClub')}</option>
                    {clubs.map((club) => (
                      <option key={club.id} value={club.id} className="bg-gray-800 text-white">
                        {club.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('auth.role')} *
                </label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
                  <select
                    value={formData.role_id}
                    onChange={(e) => handleInputChange('role_id', e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                    required
                  >
                    <option value="">{t('auth.selectRole')}</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id} className="bg-gray-800 text-white">
                        {getRoleDisplayName(role?.nombre)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 backdrop-blur-sm">
                <p className="text-sm text-red-200">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-6 text-white font-semibold rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105 focus:ring-4 focus:outline-none flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              style={{ background: '#0F2A7D' }}
              onMouseEnter={(e) => !isLoading && (e.currentTarget.style.background = '#0A1F5C')}
              onMouseLeave={(e) => e.currentTarget.style.background = '#0F2A7D'}
            >
              {isLoading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <span>{t('auth.createAccount')}</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-blue-200 text-sm">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link
                to="/login"
                className="text-blue-300 font-medium hover:text-blue-200 transition-colors underline"
              >
                {t('auth.login')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
